import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Drawer, IconButton, Slide } from '@mui/material';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from '@mui/icons-material/Close';
import StorePageHeader from 'Components/StorePageHeader';
import BaseModal from 'Components/BaseModal';
import { translateLabel } from 'services/Utilities/labelUtils';
import {
  useActiveCart,
  usePostBrpClubCartItem,
  usePostBrpClubCartTooling,
  usePostCarItem,
  usePostCartTooling,
} from 'services/Data/CartService';
import {
  useBrpClubDealerPortalFilters,
  useDealerPortalFilters,
  useDealerPortalMenuProductsInfinite,
} from 'services/Data/DealerPortalMenuServices';
import useImage from 'services/Hooks/useImage';
import { DealerPortalMenuProductTypeEnumValue } from 'enums/DealerPortalMenuProductTypeEnum';
import { DealerPortalSectionEnum, getDealerPortalSectionValue } from 'enums/DealerPortalSectionEnum';
import CartRedirectionModal from '../Components/CartRedirectionModal';
import InventoryItemCategory from '../InventoryItemCategories/InventoryItemCategory';
import DealerPortalMenuFilterSidebar from './Components/DealerPortalMenuFilterSidebar';
import DealerPortalMenuProductResultsPanel from './Components/DealerPortalMenuProductResultsPanel';
import { IsClub } from 'config/config';

const normalizeCollection = collectionOrWrapped => {
  if (Array.isArray(collectionOrWrapped)) {
    return collectionOrWrapped;
  }

  if (Array.isArray(collectionOrWrapped?.items)) {
    return collectionOrWrapped.items;
  }

  return [];
};

const parseNumberValue = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const parseNullableNumberValue = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return parseNumberValue(value);
};

const getProductType = product => product?.productType;

const DealerPortalMenuProducts = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { dealerPortalMenuId, sectionName } = useParams();
  const accountReducer = useSelector(state => state.AccountReducer);
  const sectionValue = getDealerPortalSectionValue(sectionName);
  const sectionConfig = DealerPortalSectionEnum.find(section => section.value === sectionValue);
  const showFiltersForSection = Boolean(sectionConfig?.showFilter);
  const {
    getInventoryItemImage,
    getToolingImage,
    getPanelTypeImage,
    getExteriorSignageImage,
    getMerchandisingProgramImage,
  } = useImage();

  const [showRedirectionModal, setShowRedirectionModal] = useState(false);
  const [quickShopSelection, setQuickShopSelection] = useState(null);
  const [selectedLeafFilterIds, setSelectedLeafFilterIds] = useState([]);
  const [collapsedFilterNodeIds, setCollapsedFilterNodeIds] = useState([]);
  const loadMoreAnchorRef = useRef(null);
  const [tableDataParams, setTableDataParams] = useState({
    currentPage: 1,
    pageSize: 24,
    search: '',
    sorts: [
      {
        value: 'menuOrder',
        name: t('order'),
        column: 'Order',
        asc: true,
      },
    ],
  });

  const serverTableDataParams = useMemo(
    () => ({
      pageSize: 24,
      search: tableDataParams.search || '',
      sorts: (tableDataParams.sorts || []).map(sortOption => ({
        column: sortOption?.column || 'Order',
        asc: sortOption?.asc !== false,
      })),
    }),
    [tableDataParams.search, tableDataParams.sorts],
  );

  const {
    data: menuProductsPagesData,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useDealerPortalMenuProductsInfinite(
    dealerPortalMenuId,
    serverTableDataParams,
    selectedLeafFilterIds,
    accountReducer.isLoggedIn,
  );
  const { data: menuFiltersData, isLoading: isFiltersLoading } = useDealerPortalFilters(
    dealerPortalMenuId,
    { selectedFilterIds: selectedLeafFilterIds },
    accountReducer.isLoggedIn && showFiltersForSection && !IsClub,
  );
  const { data: cart } = useActiveCart(accountReducer.isLoggedIn);
  const [itemToReplace, setItemToReplace] = useState(null);
  const { mutate: addCartItem } = usePostCarItem(accountReducer.isDemo);
  const { mutate: addCartTooling } = usePostCartTooling(accountReducer.isDemo);
  const { mutate: addBrpClubCartItem } = usePostBrpClubCartItem(false);
  const { mutate: addBrpClubTooling } = usePostBrpClubCartTooling(false);
  const { data: brpClubMenuFiltersData, isLoading: isBrpClubFiltersLoading } = useBrpClubDealerPortalFilters(
    dealerPortalMenuId,
    { selectedFilterIds: selectedLeafFilterIds },
    accountReducer.isLoggedIn && showFiltersForSection && IsClub,
  );

  const buildMenuProductDetailRoute = useCallback(
    productId => `/dealer/section/${sectionName}/menu/${dealerPortalMenuId}/products/${productId}`,
    [sectionName, dealerPortalMenuId],
  );

  const resolvePreviewImageId = useCallback(product => {
    const previewImageId = product?.previewImageId;
    if (previewImageId) {
      return previewImageId;
    }

    const productType = getProductType(product);
    if (productType === DealerPortalMenuProductTypeEnumValue.InventoryItem) {
      const images = normalizeCollection(product?.inventoryItem?.images);
      return images.find(image => image?.dealerPortalPrimary)?.id || images[0]?.id;
    }

    if (productType === DealerPortalMenuProductTypeEnumValue.Tooling) {
      const images = normalizeCollection(product?.tooling?.images);
      return images.find(image => image?.dealerPortalPrimary)?.id || images[0]?.id;
    }

    if (productType === DealerPortalMenuProductTypeEnumValue.ExteriorSignage) {
      const images = normalizeCollection(product?.exteriorSignage?.images);
      return images.find(image => image?.dealerPortalPrimary)?.id || images[0]?.id;
    }

    if (productType === DealerPortalMenuProductTypeEnumValue.MerchandisingProgram) {
      const images = normalizeCollection(product?.merchandisingProgram?.images);
      return images.find(image => image?.dealerPortalPrimary)?.id || images[0]?.id;
    }

    const images = normalizeCollection(product?.panelType?.images);
    return images.find(image => image?.dealerPortalPrimary)?.id || images[0]?.id;
  }, []);

  const getImageSrc = useCallback(
    product => {
      const productType = getProductType(product);
      const productId = product?.productId;
      const previewImageId = resolvePreviewImageId(product);

      if (!productId || !previewImageId) {
        return '/images/brp-logo.png';
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.InventoryItem) {
        return getInventoryItemImage(productId, previewImageId);
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.Tooling) {
        return getToolingImage(productId, previewImageId);
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.PanelType) {
        return getPanelTypeImage(productId, previewImageId);
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.ExteriorSignage) {
        return getExteriorSignageImage(productId, previewImageId);
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.MerchandisingProgram) {
        return getMerchandisingProgramImage(productId, previewImageId);
      }

      return '/images/brp-logo.png';
    },
    [
      getInventoryItemImage,
      getToolingImage,
      getPanelTypeImage,
      getExteriorSignageImage,
      getMerchandisingProgramImage,
      resolvePreviewImageId,
    ],
  );

  const menuProductsData = useMemo(() => {
    const pages = normalizeCollection(menuProductsPagesData?.pages);
    if (pages.length === 0) {
      return null;
    }

    const firstPage = pages[0] || {};
    const products = pages.flatMap(page => normalizeCollection(page?.items));

    return {
      dealerPortalMenuId: firstPage?.dealerPortalMenuId,
      menuName: firstPage?.menuName,
      count: Number(firstPage?.count ?? 0),
      products,
    };
  }, [menuProductsPagesData]);

  useEffect(() => {
    const anchorNode = loadMoreAnchorRef.current;
    if (!anchorNode || !hasNextPage || isFetchingNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '300px 0px' },
    );

    observer.observe(anchorNode);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, menuProductsData?.products?.length]);

  const configuredProducts = useMemo(() => {
    return normalizeCollection(menuProductsData?.products)
      .map(product => {
        const productType = getProductType(product);
        const productId = product?.productId;
        const usefulLink = product?.usefulLink || null;

        const promotionQuantity = parseNumberValue(product?.promotionQuantity);
        const inventoriedQuantityAvailable = parseNullableNumberValue(product?.inventoriedQuantityAvailable);
        const isInPromotion = Boolean(product?.isInPromotion);
        const quantityInCart =
          cart?.cartItems?.find(cartItem => cartItem.inventoryItem_Id === productId || cartItem.tooling_Id === productId)
            ?.quantity ?? 0;

        return {
          id: product?.id || `${productType}:${productId}`,
          order: parseNumberValue(product?.order),
          productId,
          productType,
          name: product?.name || '',
          description: product?.description || '',
          shortDescription: product?.shortDescription || '',
          sku: product?.sku || null,
          price: parseNumberValue(product?.price),
          promotionPrice: parseNumberValue(product?.promotionPrice),
          currencyName: product?.currencyName || null,
          quantityPerPackage: parseNumberValue(product?.quantityPerPackage),
          promotionQuantity,
          inventoriedQuantityAvailable,
          isInPromotion,
          hasAdditionalToolings: Boolean(product?.hasAdditionalToolings),
          isPromoLimitReached: isInPromotion && promotionQuantity > 0 && quantityInCart >= promotionQuantity,
          isInventoriedLimitReached:
            inventoriedQuantityAvailable != null &&
            inventoriedQuantityAvailable >= 0 &&
            quantityInCart >= inventoriedQuantityAvailable,
          inventoryItem: product?.inventoryItem || null,
          tooling: product?.tooling || null,
          panelType: product?.panelType || null,
          exteriorSignage: product?.exteriorSignage || null,
          merchandisingProgram: product?.merchandisingProgram || null,
          usefulLink,
          imgSrc: getImageSrc(product),
        };
      })
      .filter(product => {
        if (!product.productId || !product.productType) {
          return false;
        }

        return true;
      });
  }, [menuProductsData, cart?.cartItems, getImageSrc]);

  const menuFilters = useMemo(() => normalizeCollection(IsClub ? brpClubMenuFiltersData : menuFiltersData), [
    brpClubMenuFiltersData,
    menuFiltersData,
  ]);

  const getFilterLabel = useCallback(
    filter => translateLabel(filter?.name, i18n.language) || translateLabel(filter?.name, 'en') || filter?.id || '',
    [i18n.language],
  );

  const leafFilters = useMemo(() => {
    const leaves = [];

    const walk = (nodes, ancestorIds = []) => {
      nodes.forEach(filter => {
        const filterId = filter?.id;
        if (!filterId) {
          return;
        }

        const children = normalizeCollection(filter?.children);
        const nextAncestorIds = [...ancestorIds, filterId];

        if (children.length === 0) {
          leaves.push({
            id: filterId,
            label: getFilterLabel(filter),
            ancestorIds,
          });

          return;
        }

        walk(children, nextAncestorIds);
      });
    };

    walk(menuFilters, []);
    return leaves;
  }, [getFilterLabel, menuFilters]);

  const leafFilterById = useMemo(() => {
    const map = new Map();
    leafFilters.forEach(filter => {
      map.set(filter.id, filter);
    });
    return map;
  }, [leafFilters]);

  useEffect(() => {
    if (leafFilterById.size === 0) {
      return;
    }

    setSelectedLeafFilterIds(currentIds => currentIds.filter(id => leafFilterById.has(id)));
  }, [leafFilterById]);

  const visibleFilterTree = useMemo(() => {
    if (!showFiltersForSection) {
      return [];
    }

    const filterNodes = nodes =>
      nodes
        .map(node => {
          const nodeId = node?.id;
          const children = normalizeCollection(node?.children);
          const visibleChildren = filterNodes(children);

          return {
            ...node,
            id: nodeId,
            children: visibleChildren,
          };
        })
        .sort((left, right) => Number(left?.order || 0) - Number(right?.order || 0));

    return filterNodes(menuFilters);
  }, [menuFilters, showFiltersForSection]);

  const allExpandableFilterNodeIds = useMemo(() => {
    const ids = [];

    const collect = nodes => {
      nodes.forEach(node => {
        if (normalizeCollection(node?.children).length > 0) {
          ids.push(node.id);
          collect(normalizeCollection(node.children));
        }
      });
    };

    collect(visibleFilterTree);
    return ids;
  }, [visibleFilterTree]);

  const expandedFilterNodeIds = useMemo(
    () => allExpandableFilterNodeIds.filter(nodeId => !collapsedFilterNodeIds.includes(nodeId)),
    [allExpandableFilterNodeIds, collapsedFilterNodeIds],
  );

  const toggleFilterNodeExpansion = useCallback(nodeId => {
    if (!nodeId) {
      return;
    }

    setCollapsedFilterNodeIds(currentIds =>
      currentIds.includes(nodeId) ? currentIds.filter(existingId => existingId !== nodeId) : [...currentIds, nodeId],
    );
  }, []);

  useEffect(() => {
    setSelectedLeafFilterIds([]);
    setCollapsedFilterNodeIds([]);
  }, [dealerPortalMenuId]);

  const activeFilterChips = useMemo(
    () => selectedLeafFilterIds.map(filterId => leafFilterById.get(filterId)).filter(Boolean),
    [leafFilterById, selectedLeafFilterIds],
  );

  const toggleLeafFilter = useCallback(filterId => {
    if (!filterId) {
      return;
    }

    setSelectedLeafFilterIds(currentIds =>
      currentIds.includes(filterId) ? currentIds.filter(id => id !== filterId) : [...currentIds, filterId],
    );
  }, []);

  const clearSelectedLeafFilters = useCallback(() => {
    setSelectedLeafFilterIds([]);
  }, []);

  const handleAddInventoryItemToCart = useCallback(
    product => {
      if (product?.isInventoriedLimitReached && product?.inventoryItemOnceDepleted_Id) {
        setItemToReplace(product);
        return;
      }

      const images = normalizeCollection(product?.inventoryItem?.images);
      const cartItem = {
        id: uuidv4(),
        inventoryItem_Id: product.productId,
        unitPrice: product.price,
        currencyName: product.currencyName,
        quantity: 1,
        cart_Id: cart?.id,
        unitPricePromotion: product.promotionPrice,
        promotionQuantity: product.promotionQuantity,
        isInPromotion: product.isInPromotion,
        imgSrc: images.find(image => image?.dealerPortalPrimary) || images[0],
        name: product.name,
      };

      if (IsClub) {
        addBrpClubCartItem(cartItem);
        setShowRedirectionModal(true);
        return;
      }
      addCartItem(cartItem);
      setShowRedirectionModal(true);
    },
    [addCartItem, addBrpClubCartItem, cart?.id],
  );

  const handleCloseReplacementModal = useCallback(() => {
    setItemToReplace(null);
  }, []);

  const handleConfirmReplacementAdd = useCallback(() => {
    if (!itemToReplace?.inventoryItemOnceDepleted_Id) {
      return;
    }

    const replacementCartItem = {
      id: uuidv4(),
      inventoryItem_Id: itemToReplace.inventoryItemOnceDepleted_Id,
      quantity: 1,
      cart_Id: cart?.id,
    };

    if (IsClub) {
      addBrpClubCartItem(replacementCartItem);
    } else {
      addCartItem(replacementCartItem);
    }

    setItemToReplace(null);
    setShowRedirectionModal(true);
  }, [addBrpClubCartItem, addCartItem, cart?.id, itemToReplace]);

  const handleAddToolingToCart = useCallback(
    product => {
      const images = normalizeCollection(product?.tooling?.images);
      const cartTooling = {
        id: uuidv4(),
        dateCreated: new Date(),
        quantity: 1,
        tooling_Id: product.productId,
        subTotal: product.price,
        price: product.price,
        name: product.name,
        imgSrc: images.find(image => image?.dealerPortalPrimary) || images[0],
        cart_Id: cart?.id,
      };
      if (IsClub) {
        addBrpClubTooling(cartTooling);
        setShowRedirectionModal(true);
        return;
      }
      addCartTooling(cartTooling);
      setShowRedirectionModal(true);
    },
    [addCartTooling, addBrpClubTooling, cart?.id],
  );

  const menuTitle =
    translateLabel(menuProductsData?.menuName, i18n.language) || menuProductsData?.dealerPortalMenuId || t('products');

  const isLoading = isProductsLoading && !menuProductsData;
  const isFiltering = isProductsFetching && !isLoading && !isFetchingNextPage;

  const sortOptions = useMemo(
    () => [
      {
        value: 'menuOrder',
        name: t('filter_order'),
        column: 'Order',
        asc: true,
      },
      {
        value: 'nameAsc',
        name: t('nameAsc'),
        column: 'Name',
        asc: true,
      },
      {
        value: 'nameDesc',
        name: t('nameDesc'),
        column: 'Name',
        asc: false,
      },
      {
        value: 'priceAsc',
        name: `${t('priceAsc')}`,
        column: 'Price',
        asc: true,
      },
      {
        value: 'priceDesc',
        name: `${t('priceDesc')}`,
        column: 'Price',
        asc: false,
      },
    ],
    [t],
  );

  const totalItemCount = Number(menuProductsData?.count ?? 0);

  const displayedRangeLabel = useMemo(() => {
    if (totalItemCount <= 0) {
      return `0 ${t('of')} 0`;
    }

    if (configuredProducts.length <= 0) {
      return `0 ${t('of')} ${totalItemCount}`;
    }

    return `1-${configuredProducts.length} ${t('of')} ${totalItemCount}`;
  }, [configuredProducts.length, t, totalItemCount]);

  const activeSearchFilter = (tableDataParams.search || '').trim();

  const handleClearSearchFilter = useCallback(() => {
    setTableDataParams(current => ({ ...current, search: '', currentPage: 1 }));
  }, []);

  const handleOpenInventoryItemQuickShop = useCallback(product => {
    if (!product?.productId) {
      return;
    }

    setQuickShopSelection(product);
  }, []);

  const handleCloseQuickShop = useCallback(() => {
    setQuickShopSelection(null);
  }, []);

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box sx={{ width: '100%', overflowX: 'hidden' }}>
        <BaseModal
          show={!!itemToReplace}
          title={t('replaceItem')}
          message={t('inventoriedItemReplacementOfferText', {
            currentItem: itemToReplace?.name || itemToReplace?.sku || '',
          })}
          closeBtnMessage={t('close')}
          onClose={handleCloseReplacementModal}
          actions={[
            {
              color: 'secondary',
              variant: 'contained',
              onClick: handleConfirmReplacementAdd,
              message: t('replaceItem'),
            },
          ]}
        />
        {showRedirectionModal && (
          <CartRedirectionModal
            show={showRedirectionModal}
            onClose={() => setShowRedirectionModal(false)}
            redirectLink={'/dealer/cart'}
            title={t('itemAddedToCart')}
            message={t('itemAddedToCartText')}
          />
        )}

        <StorePageHeader title={menuTitle} backgroundColor="lightGray" borderRadius="0.5rem" />
        <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
            <DealerPortalMenuFilterSidebar
              showFiltersForSection={showFiltersForSection}
              selectedLeafFilterIds={selectedLeafFilterIds}
              clearSelectedLeafFilters={clearSelectedLeafFilters}
              activeFilterChips={activeFilterChips}
              isFiltersLoading={IsClub ? isBrpClubFiltersLoading : isFiltersLoading}
              visibleFilterTree={visibleFilterTree}
              expandedFilterNodeIds={expandedFilterNodeIds}
              onToggleFilterNode={toggleFilterNodeExpansion}
              toggleLeafFilter={toggleLeafFilter}
              getFilterLabel={getFilterLabel}
              titleLabel={t('dealerPortal.filters.title')}
              clearAllLabel={t('dealerPortal.filters.clearAll')}
              selectedLabel={t('dealerPortal.filters.selected')}
              loadingLabel={t('common.loading')}
              noneAvailableLabel={t('dealerPortal.filters.noneAvailable')}
            />

            <DealerPortalMenuProductResultsPanel
              showFiltersForSection={showFiltersForSection}
              tableDataParams={tableDataParams}
              setTableDataParams={setTableDataParams}
              isLoading={isLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              isFiltering={isFiltering}
              loadMoreAnchorRef={loadMoreAnchorRef}
              sortOptions={sortOptions}
              activeSearchFilter={activeSearchFilter}
              onClearSearchFilter={handleClearSearchFilter}
              filterLabel={t('common.filter')}
              clearLabel={t('common.clear')}
              noItemsLabel={t('noItemsToDisplay')}
              loadingMoreLabel={t('common.loading')}
              displayedRangeLabel={displayedRangeLabel}
              filteredSortedProducts={configuredProducts}
              buildMenuProductDetailRoute={buildMenuProductDetailRoute}
              onNavigateToRoute={route => history.push(route)}
              onAddInventoryItemToCart={handleAddInventoryItemToCart}
              onAddToolingToCart={handleAddToolingToCart}
              onOpenInventoryItemQuickShop={handleOpenInventoryItemQuickShop}
            />
          </Box>
        </Box>

        <Drawer
          anchor="right"
          open={Boolean(quickShopSelection)}
          onClose={handleCloseQuickShop}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: '80%', md: '80%', lg: '55%' },
              maxWidth: '100%',
              backgroundColor: theme => theme.palette.background.default,
            },
          }}>
          <Box sx={{ height: '100%', overflowY: 'auto', px: { xs: 2, md: 4 }, py: 2 }}>
            <Box display="flex" justifyContent="flex-end">
              <IconButton onClick={handleCloseQuickShop} aria-label={t('close')}>
                <CloseIcon />
              </IconButton>
            </Box>
            {quickShopSelection && (
              <InventoryItemCategory inventoryItemIdOverride={quickShopSelection.productId} isQuickShop />
            )}
          </Box>
        </Drawer>
      </Box>
    </Slide>
  );
};

export default DealerPortalMenuProducts;
